/**
 * «Записи» → «Воронка» (`?tab=funnel`, `GET /api/appointments/funnel`): каким маршрутом сделки
 * доходят до стадии «Ждем клиента» и какая процентовка маршрутов. Клик по строке — сделки этой
 * группы с переходом в «Чаты» (там видно, что писали) и в карточку сделки (там расшифровки
 * звонков). Спека `docs/specs/2026-10-10-appointments-funnel-design.md`.
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { DateRangePicker } from "@/components/DateRangePicker";
import { apiFetch } from "@/lib/api";
import { iso, plusDays } from "@/lib/dates";

type Numbers = {
  records: number;
  share: number;
  came: number;
  came_pct: number;
  booked: number;
  booked_pct: number;
  median_h: number | null;
  p75_h: number | null;
  live_median_h: number | null;
  touches_median: number | null;
  buckets: Record<string, number>;
};
type Row = Numbers & { key: string; label: string; routes?: number };
type Funnel = {
  from: string;
  to: string;
  recorded: number;
  by: string;
  by_label: string;
  cuts: { key: string; label: string; hint: string }[];
  buckets: { key: string; label: string }[];
  total: Numbers;
  rows: Row[];
};
type Deal = {
  zapis_at: string;
  phones: number[];
  deal_id: number | null;
  lead_id: number | null;
  chat_id: number | null;
  call_id: number | null;
  route: string;
  hours: number | null;
  touches: number;
  method_label: string;
  where: string;
  user_name: string | null;
  status: string;
  reason_label: string;
  visit_at: string | null;
  booking_at: string | null;
};
type Deals = { by: string; key: string; label: string; total: number; deals: Deal[] };

const card = "rounded-[var(--radius-card)] border border-border bg-bg-subtle";
const th = "whitespace-nowrap border-b border-border px-2.5 py-2 text-left text-[11.5px] font-medium text-fg-muted";
const td = "whitespace-nowrap px-2.5 py-1.5 tabular-nums";
const ИСХОД: Record<string, string> = { booked: "забронировали", came: "пришли без брони", no_show: "не пришли" };

const seg = "h-7 rounded-[var(--radius-ui)] border border-border px-2.5 text-[12.5px] text-fg-muted hover:text-fg";
const днейМежду = (a: string, b: string) => Math.round((Date.parse(`${b}T12:00:00`) - Date.parse(`${a}T12:00:00`)) / 86_400_000);

/** Второй отрезок: выбор «Сравнить с» или быстрые кнопки «прошлый такой же отрезок» / «месяц назад». */
export function FunnelCompare({
  from,
  to,
  cfrom,
  cto,
  onChange,
}: {
  from: string;
  to: string;
  cfrom: string;
  cto: string;
  onChange: (from?: string, to?: string) => void;
}) {
  if (cfrom && cto)
    return (
      <div className="flex items-center gap-1" data-annot="сравнение воронки">
        <DateRangePicker label="Сравнить с" from={cfrom} to={cto} onChange={(a, b) => onChange(a, b)} />
        <button
          type="button"
          onClick={() => onChange(undefined, undefined)}
          className="grid h-7 w-7 place-items-center rounded-[var(--radius-ui)] text-fg-muted hover:text-fg"
          aria-label="Убрать сравнение"
          title="Убрать сравнение"
        >
          <X size={14} />
        </button>
      </div>
    );
  const start = new Date(`${from}T12:00:00`);
  const n = днейМежду(from, to) + 1;
  return (
    <div className="flex gap-1" data-annot="сравнение воронки">
      <button type="button" className={seg} onClick={() => onChange(iso(plusDays(start, -n)), iso(plusDays(start, -1)))}>
        + с предыдущим отрезком
      </button>
      <button
        type="button"
        className={seg}
        onClick={() => onChange(iso(plusDays(start, -28)), iso(plusDays(new Date(`${to}T12:00:00`), -28)))}
        title="тот же отрезок 4 недели назад — те же дни недели"
      >
        + 4 недели назад
      </button>
    </div>
  );
}

/** Изменение доли в процентных пунктах: «+3,2», зелёным рост, красным падение. */
function Сдвиг({ now, was }: { now: number; was: number }) {
  const d = Math.round((now - was) * 10) / 10;
  if (Math.abs(d) < 0.5) return <span className="text-fg-faint">≈</span>;
  return (
    <span className={d > 0 ? "text-ok" : "text-err"}>
      {d > 0 ? "+" : "−"}
      {Math.abs(d).toFixed(1).replace(".", ",")}
    </span>
  );
}

const ПУСТО: Numbers = {
  records: 0,
  share: 0,
  came: 0,
  came_pct: 0,
  booked: 0,
  booked_pct: 0,
  median_h: null,
  p75_h: null,
  live_median_h: null,
  touches_median: null,
  buckets: {},
};

/** 0.08 → «5 мин», 6 → «6 ч», 347 → «14 дн». */
function срок(h: number | null): string {
  if (h === null) return "—";
  if (h < 1) return `${Math.max(Math.round(h * 60), 1)} мин`;
  if (h < 48) return `${h < 10 ? h.toFixed(1).replace(".", ",") : Math.round(h)} ч`;
  return `${Math.round(h / 24)} дн`;
}

/** Маршрут строкой: шаги подсвечены по типу, стрелки серые. */
function Route({ label }: { label: string }) {
  const тон = (ш: string) =>
    ш.startsWith("рассылка")
      ? "text-accent"
      : ш.startsWith("переписка")
        ? "text-ok"
        : ш.startsWith("входящий") || ш.startsWith("исходящий")
          ? "text-fg"
          : ш.startsWith("не дозвонились")
            ? "text-err"
            : "text-warn";
  return (
    <span className="inline-flex flex-wrap items-baseline gap-1">
      {label.split(" → ").map((ш, i) => (
        <span key={`${ш}-${i}`} className="inline-flex items-baseline gap-1">
          {i > 0 && <span className="text-fg-faint">→</span>}
          <span className={тон(ш)}>{ш}</span>
        </span>
      ))}
    </span>
  );
}

function DealList({ from, to, by, row }: { from: string; to: string; by: string; row: Row }) {
  const q = useQuery({
    queryKey: ["funnel-deals", from, to, by, row.key],
    queryFn: () =>
      apiFetch<Deals>(`/api/appointments/funnel/deals?from=${from}&to=${to}&by=${by}&key=${encodeURIComponent(row.key)}`),
  });
  if (q.isPending) return <p className="px-4 py-2 text-[12.5px] text-fg-muted">Загружаю сделки…</p>;
  if (q.error) return <p className="px-4 py-2 text-[12.5px] text-err">{String(q.error)}</p>;
  const d = q.data;
  return (
    <div className="border-t border-border bg-bg px-0 py-0" data-annot="сделки маршрута">
      <div className="px-4 py-2 text-[12px] text-fg-muted">
        {d.label} — {d.total} записей{d.deals.length < d.total ? `, показаны первые ${d.deals.length}` : ""}. Клик по
        «чат» — переписка клиента, по номеру сделки — карточка с расшифровками звонков.
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead>
            <tr>
              <th className={`${th} pl-4`}>Записали</th>
              <th className={th}>Номер</th>
              <th className={th}>Маршрут подробно</th>
              <th className={th}>Срок</th>
              <th className={th}>Касаний</th>
              <th className={th}>Способ</th>
              <th className={th}>Кто</th>
              <th className={th}>Итог</th>
              <th className={th}>Куда смотреть</th>
            </tr>
          </thead>
          <tbody>
            {d.deals.map((x, i) => (
              <tr key={`${x.deal_id}-${i}`} className="border-b border-border last:border-b-0 hover:bg-bg-hover">
                <td className={`${td} pl-4`}>{x.zapis_at.slice(0, 16)}</td>
                <td className={`${td} font-mono`}>
                  {x.phones.length ? (
                    <Link to={`/search?q=${x.phones[0]}`} className="hover:underline">
                      {x.phones[0]}
                    </Link>
                  ) : (
                    <span className="text-fg-faint">—</span>
                  )}
                </td>
                <td className="px-2.5 py-1.5">
                  <Route label={x.route} />
                </td>
                <td className={td}>{срок(x.hours)}</td>
                <td className={td}>{x.touches}</td>
                <td className="whitespace-nowrap px-2.5 py-1.5">{x.method_label}</td>
                <td className="whitespace-nowrap px-2.5 py-1.5 text-fg-muted">{x.user_name ?? "—"}</td>
                <td className="whitespace-nowrap px-2.5 py-1.5">
                  {x.status === "no_show" ? <span className="text-fg-muted">{x.reason_label}</span> : ИСХОД[x.status]}
                </td>
                <td className="whitespace-nowrap px-2.5 py-1.5">
                  <span className="flex gap-2">
                    {x.chat_id ? (
                      <Link to={`/chats/${x.chat_id}`} className="text-accent hover:underline" title="переписка клиента">
                        чат
                      </Link>
                    ) : null}
                    {x.deal_id ? (
                      <Link to={`/deals/${x.deal_id}`} className="font-mono hover:underline" title="карточка сделки">
                        {x.deal_id}
                      </Link>
                    ) : null}
                    {x.call_id ? (
                      <Link to={`/calls/${x.call_id}`} className="text-fg-muted hover:underline" title="звонок с расшифровкой">
                        звонок
                      </Link>
                    ) : null}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function FunnelView({ from, to, cfrom, cto }: { from: string; to: string; cfrom: string; cto: string }) {
  const [by, setBy] = useState("route");
  const [open, setOpen] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ["funnel", from, to, by],
    queryFn: () => apiFetch<Funnel>(`/api/appointments/funnel?from=${from}&to=${to}&by=${by}`),
    // Пока считается новый разрез, прежняя таблица остаётся на экране с пометкой «считаю…».
    placeholderData: keepPreviousData,
  });
  const сравнение = Boolean(cfrom && cto);
  const cq = useQuery({
    queryKey: ["funnel", cfrom, cto, by],
    queryFn: () => apiFetch<Funnel>(`/api/appointments/funnel?from=${cfrom}&to=${cto}&by=${by}`),
    enabled: сравнение,
    placeholderData: keepPreviousData,
  });
  if (q.isPending) return <p className="text-fg-muted">Загружаю…</p>;
  if (q.error) return <p className="text-err">{String(q.error)}</p>;
  const d = q.data;
  const t = d.total;
  // Сравнение показываем, только когда второй отрезок посчитан в том же разрезе.
  const c = сравнение && cq.data && cq.data.by === d.by ? cq.data : null;
  const считаю = q.isPlaceholderData || (сравнение && (cq.isPending || cq.isPlaceholderData));
  const выбран = d.cuts.find((x) => x.key === by);
  const было = new Map((c?.rows ?? []).map((r) => [r.key, r]));
  // Строки, которых в основном отрезке нет, а во втором есть, — внизу с нулями.
  const строки: Row[] = [
    ...d.rows,
    ...(c?.rows ?? [])
      .filter((r) => !d.rows.some((x) => x.key === r.key))
      .map((r): Row => ({ ...ПУСТО, key: r.key, label: r.label })),
  ];
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2" data-annot="разрезы воронки">
        <span className="text-[12.5px] text-fg-muted">Группировка:</span>
        {d.cuts.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => {
              setBy(c.key);
              setOpen(null);
            }}
            className={`rounded-full border px-2.5 py-0.5 text-[12px] ${
              by === c.key ? "border-accent bg-bg font-medium text-fg" : "border-border text-fg-muted hover:text-fg"
            }`}
            data-annot={`разрез «${c.label}»`}
          >
            {c.label}
          </button>
        ))}
        {считаю && <span className="text-[12px] text-fg-muted">считаю…</span>}
      </div>
      {выбран?.hint && (
        <p className="-mt-1 text-[12.5px] text-fg-muted" data-annot="пояснение разреза">
          <b className="font-medium text-fg">{выбран.label}:</b> {выбран.hint}
        </p>
      )}
      <p className={`${card} px-4 py-2.5 text-[12.5px] text-fg-muted`}>
        Записей за отрезок — <b className="text-fg">{t.records}</b>, те же, что на других вкладках «Записей» (
        {d.recorded}). Маршрут — что было с клиентом ДО того, как сделка вошла в «Ждем клиента»: рассылки, открытия
        ссылки, переписка, звонки. Срок считается от первого шага маршрута, а у лида — от его создания. Запись
        попадает в отрезок по дню, когда сделка вошла в «Ждем клиента»; отрезок — до 92 дней.
        {c && (
          <>
            {" "}
            Сравнение: {c.from.split("-").reverse().join(".")}–{c.to.split("-").reverse().join(".")} —{" "}
            <b className="text-fg">{c.total.records}</b> записей; сравниваем доли, а не штуки: отрезки бывают разной
            длины.
          </>
        )}
        {cq.error && <span className="text-err"> Сравнение не посчиталось: {String(cq.error)}</span>}
      </p>
      <section className={`${card} ${считаю ? "opacity-50" : ""}`} data-annot="таблица воронки">
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr>
                <th className={`${th} pl-4`}>{d.by_label}</th>
                <th className={th}>Записей</th>
                <th className={th}>Доля</th>
                {c && (
                  <>
                    <th className={th} title="записей и доля во втором отрезке">
                      Было
                    </th>
                    <th className={th} title="изменение доли, процентных пунктов">
                      Δ доли
                    </th>
                  </>
                )}
                <th className={th}>Пришли</th>
                <th className={th}>Бронь</th>
                <th className={th} title="медиана срока от первого шага до записи">
                  Медиана
                </th>
                <th className={th} title="75-я процентиль: столько ждут самые долгие четверть">
                  p75
                </th>
                <th className={th} title="медиана числа наших касаний до записи">
                  Касаний
                </th>
                {d.buckets.map((b) => (
                  <th key={b.key} className={th}>
                    {b.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {строки.map((r) => (
                <>
                  <tr
                    key={r.key}
                    onClick={() => setOpen(open === r.key ? null : r.key)}
                    className={`cursor-pointer border-b border-border hover:bg-bg-hover ${open === r.key ? "bg-bg-hover" : ""}`}
                    data-annot={`строка воронки «${r.label}»`}
                  >
                    <td className="px-2.5 py-1.5 pl-4">
                      {d.by === "route" ? <Route label={r.label} /> : r.label}
                      {r.routes ? <span className="ml-2 text-[11px] text-fg-faint">{r.routes} маршрутов</span> : null}
                    </td>
                    <td className={`${td} font-semibold`}>{r.records}</td>
                    <td className={td}>{r.share}%</td>
                    {c && (
                      <>
                        <td className={`${td} text-fg-muted`}>
                          {было.get(r.key)?.records ?? 0}
                          <span className="ml-1.5 text-[11.5px] text-fg-faint">{было.get(r.key)?.share ?? 0}%</span>
                        </td>
                        <td className={td}>
                          <Сдвиг now={r.share} was={было.get(r.key)?.share ?? 0} />
                        </td>
                      </>
                    )}
                    <td className={td}>
                      {r.came}
                      <span className="ml-1.5 text-[11.5px] text-fg-faint">{r.came_pct}%</span>
                    </td>
                    <td className={`${td} font-medium`}>
                      {r.booked || <span className="text-fg-faint">·</span>}
                      {r.booked ? <span className="ml-1.5 text-[11.5px] text-fg-faint">{r.booked_pct}%</span> : null}
                    </td>
                    <td className={td}>{срок(r.median_h)}</td>
                    <td className={`${td} text-fg-muted`}>{срок(r.p75_h)}</td>
                    <td className={td}>{r.touches_median ?? "—"}</td>
                    {d.buckets.map((b) => (
                      <td key={b.key} className={`${td} text-fg-muted`}>
                        {r.buckets[b.key] || <span className="text-fg-faint">·</span>}
                      </td>
                    ))}
                  </tr>
                  {open === r.key && (
                    <tr key={`${r.key}-deals`}>
                      <td colSpan={8 + (c ? 2 : 0) + d.buckets.length} className="p-0">
                        <DealList from={from} to={to} by={d.by} row={r} />
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
            <tfoot className="border-t border-border-strong font-medium">
              <tr>
                <td className="px-2.5 py-2 pl-4">Итого</td>
                <td className={td}>{t.records}</td>
                <td className={td}>100%</td>
                {c && (
                  <>
                    <td className={`${td} text-fg-muted`}>{c.total.records}</td>
                    <td className={td} />
                  </>
                )}
                <td className={td}>
                  {t.came}
                  <span className="ml-1.5 text-[11.5px] text-fg-faint">{t.came_pct}%</span>
                </td>
                <td className={td}>
                  {t.booked}
                  <span className="ml-1.5 text-[11.5px] text-fg-faint">{t.booked_pct}%</span>
                </td>
                <td className={td}>{срок(t.median_h)}</td>
                <td className={td}>{срок(t.p75_h)}</td>
                <td className={td}>{t.touches_median ?? "—"}</td>
                {d.buckets.map((b) => (
                  <td key={b.key} className={td}>
                    {t.buckets[b.key] || "·"}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      </section>
    </div>
  );
}
